<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from "vue";
import SmallModel from "@/components/Interface/Modal/ModelSizes/SmallModel.vue";
import StandardCard from '@/components/Elements/Cards/StandardCard.vue';
import { useGameStore } from '@/stores/gameStore.ts';
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import Backend from '@/models/backend.ts'
import { getMessaging, getToken } from 'firebase/messaging'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore();

// Reactive current time
const now = ref(new Date());

let interval: number | undefined;
const errorMessage = ref('');
const messaging = getMessaging();
const deviceAdded = ref(false);

// Start interval on mount
onMounted(() => {
  interval = window.setInterval(() => {
    now.value = new Date();
    // If the turn > 1, we started, close the modal
    if(gameStore.turn>1) {
      gameStore.modalType=''
    }
  }, 1000);
});

// Cleanup on unmount
onUnmounted(() => {
  if (interval) {
    clearInterval(interval);
  }
});

// Format time left as HH:MM:SS
const timeLeft = computed(() => {
  const startTime = new Date(gameStore.startTime);
  const totalSeconds = Math.max(0, Math.floor((startTime.getTime() - now.value.getTime()) / 1000));

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
});

// Format local start time
const localStartTime = computed(() => {
  const startTime = new Date(gameStore.startTime);
  return startTime.toLocaleString(undefined, {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
});

async function requestPermissionAndGetToken() {
  errorMessage.value = ''
  try {
    const permission = await Notification.requestPermission()
    if (permission === 'granted') {
      const currentToken = await getToken(messaging, {
        vapidKey: 'BEHw7ok0SL3e4Nv3ZYbT3foyiZGeTvVcKi4t9KQ7-kHg6sutrL44Z7QyS7RItIjC5b_7d_NWrXmFkDLi2u8pVIk'
      })

      if (currentToken) {
        const backend = new Backend()
        await backend.postFireBaseToken(currentToken).then(() => {
          gameStore.setTinySuccessToastMessage(t('modals.startCountdown.messages.deviceAdded'))
          deviceAdded.value= true
        })
      } else {
        errorMessage.value = t('modals.startCountdown.messages.noToken')
      }
    } else {
      errorMessage.value = t('modals.startCountdown.messages.permissionDenied')
    }
  } catch (err) {
    logger.error('Error retrieving token:', err)
    errorMessage.value = t('modals.startCountdown.messages.tokenError')
  }
}

</script>


<template>
  <SmallModel :title="t('modals.startCountdown.title')">
    <StandardCard class="px-2" top-padding="2">
      {{ t('modals.startCountdown.description') }}
    </StandardCard>
    <div class="flex justify-between p-1 px-2 pb-2 text-center">
      <StandardCard class="w-1/2 text-green-500">
        <div class="">{{ timeLeft }}</div>
      </StandardCard>
      <StandardCard class="w-1/2 ps-2 text-metal-300">
        <div class="">{{ t('modals.startCountdown.startsAt', { time: localStartTime }) }}</div>
      </StandardCard>
    </div>
    <StandardCard v-if="!deviceAdded" class="px-2 pb-2 text-center" top-padding="0">
      <div class="pb-2">
        {{ t('modals.startCountdown.notifyPrompt') }}
      </div>
      <ActionButton @click="requestPermissionAndGetToken">{{ t('common.actions.notifyMe') }}</ActionButton>
      <div class="text-red-500">{{ errorMessage }}</div>
    </StandardCard>
  </SmallModel>
</template>


